import SaveIcon from '@mui/icons-material/Save';
import { Button, Skeleton, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Companies from '../../service/Companies';
import queryClient from '../../service/query';
import { Form } from '../../StyledComponents/Input/generic';
import { useMutation, useQuery } from '@tanstack/react-query';
import { error } from 'ajv/dist/vocabularies/applicator/dependencies';

const companySrvc = new Companies();

type regraType = {
  digit: number,
  lowercase: number,
  min: number,
  special: number,
  uppercase: number,
  expiration: number,
  noRepeatLastPassword: number
};

export default function Password() {
  const { t } = useTranslation('translation');
  const [regra, setRegra] = useState<regraType>({} as regraType);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['GET_REGRAS'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error(error.message as string);
        if (token) {
          const body = { ...token, empresa: token.userEmpresa };
          const response = await companySrvc.getAllPoliticaSenha(body);
          if (!response) throw new Error(error.message as string);
          return response;
        }
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always'
  },
  );

  useEffect(() => {
    if (data) {
      setRegra({ ...data });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: keyof typeof regra) => {
    setRegra({ ...regra, [key]: parseInt(e.target.value) });
  };

  const { mutate, isPending } = useMutation({
    mutationKey: ['CHANGE_REGRA'],
    mutationFn: async () => {
      const promise = toast.promise(async () => {
        try {
          const token = await AttToken();
          if (!token) throw new Error(error.message as string);
          if (token) {
            const body = { ...token, empresa: token.userEmpresa, update: regra };
            const res = await companySrvc.editaPoliticaSenha(body);
            if (res.message !== 'Politicas de senha alteradas com sucesso!') throw new Error(error.message as string);
            return res.message;
          }
        } catch (err) {
          console.error(err);
          throw err;
        }
      }, {
        pending: t('settingsPage.changingPasswordPolicies'),
        success: t('settingsPage.passwordPoliciesChangedSuccessfully'),
        error: t('settingsPage.errorChangingPasswordPolicies'),
      }, { toastId: 'altera-senha' });
      return promise;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['GET_REGRAS'] });
    }
  });

  const onLoading = isLoading || isFetching || isPending;

  return (
    <Form>
      <h1 style={{ marginBottom: '10px' }}>{t('settingsPage.password.modify')}</h1>
      <Stack
        width={'100%'}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        {onLoading
          ? <>
            <Stack
              width={'100%'}
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
              spacing={2}
            >
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
            </Stack>
            <Stack
              width={'100%'}
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={2}
            >
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton width={100} height={60} />
            </Stack>
          </>
          : <>
            <Stack
              width={'100%'}
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
              spacing={2}
            >
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minNmbr')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'digit')}
                value={regra.digit}
              />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minUpper')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'uppercase')}
                value={regra.uppercase} />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minLower')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'lowercase')}
                value={regra.lowercase} />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minSpecial')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'special')}
                value={regra.special} />
            </Stack>
            <Stack
              width={'100%'}
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={2}
            >
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minChar')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'min')}
                value={regra.min} />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.expiration')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                onChange={(e) => handleChange(e, 'expiration')}
                type='number'
                value={regra.expiration} />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.prevent')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'noRepeatLastPassword')}
                value={regra.noRepeatLastPassword} />
              <Button
                endIcon={<SaveIcon />}
                disabled={onLoading}
                className='button is-success'
                type="button"
                onClick={() => {
                  trackEventMatomo('gestão de senha', 'click', 'button', 'salva');
                  mutate();
                }}
              >
                {t('save')}
              </Button>
            </Stack>
          </>
        }
      </Stack>
    </Form>
  );
}
